import { inject, Injectable } from '@angular/core';
import { AiAssistantService } from './ai-assistant.service';
import { AiCapabilityForm, AiToolForm } from '../forms';
import { getNamespace } from './ai-assistant-utils';
import { nonNullishPropertyGuard } from '@vendasta/shared';

export interface FormSubmissionOptions {
  accountGroupId?: string;
  partnerId?: string;
}

export interface ToolUpdateResult {
  toolId: string;
  success: boolean;
}

export interface PromptModuleUpdateResult {
  promptModuleId: string;
  success: boolean;
}

export interface CapabilityUpdateResult {
  capabilityId: string;
  success: boolean;
}

@Injectable()
export class AiFormSubmissionService {
  private readonly aiAssistantService = inject(AiAssistantService);

  /**
   * Updates tools from a capability form
   */
  async updateToolsFromCapability(
    capabilityForm: AiCapabilityForm,
    options: FormSubmissionOptions,
  ): Promise<ToolUpdateResult[]> {
    return this.updateToolsFromCapabilities([capabilityForm], options);
  }

  /**
   * Updates tools from multiple capabilities (used in assistant form)
   */
  async updateToolsFromCapabilities(
    capabilities: AiCapabilityForm[],
    options: FormSubmissionOptions,
  ): Promise<ToolUpdateResult[]> {
    const localNamespace = getNamespace(options.accountGroupId, options.partnerId);
    if (!localNamespace) {
      throw new Error('Unable to determine local namespace');
    }

    const toolUpdates: Promise<ToolUpdateResult>[] = [];
    const toolFormMap = new Map<string, AiToolForm>();

    const toolsToUpdate = capabilities.flatMap((capability) => {
      return capability.controls.tools.controls
        ?.filter((tool) => tool.dirty && !tool.disabled)
        .map((tool) => {
          const toolFunction = tool.toFunction(localNamespace);
          if (toolFunction.id) {
            toolFormMap.set(toolFunction.id, tool);
          }
          return toolFunction;
        });
    });

    const uniqueToolsToUpdate = toolsToUpdate?.filter(
      (tool, index, self) => index === self.findIndex((t) => t.id === tool.id),
    );

    uniqueToolsToUpdate?.filter(nonNullishPropertyGuard('id')).forEach((tool) =>
      toolUpdates.push(
        this.aiAssistantService
          .upsertFunction(tool)
          .then(() => {
            // Mark the corresponding tool form as pristine when upsert succeeds
            const toolForm = toolFormMap.get(tool.id);
            if (toolForm) {
              toolForm.markAsPristine();
            }
            return { toolId: tool.id, success: true };
          })
          .catch(() => {
            return { toolId: tool.id, success: false };
          }),
      ),
    );

    return Promise.all(toolUpdates);
  }

  /**
   * Updates prompt modules from a capability form
   */
  async updatePromptModulesFromCapability(
    capabilityForm: AiCapabilityForm,
    options: FormSubmissionOptions,
  ): Promise<PromptModuleUpdateResult[]> {
    const localNamespace = getNamespace(options.accountGroupId, options.partnerId);
    if (!localNamespace) {
      throw new Error('Unable to determine local namespace');
    }

    const promptModuleUpdates: Promise<PromptModuleUpdateResult>[] = [];
    const promptModuleForms = capabilityForm.controls.promptModules;

    promptModuleForms.controls.forEach((promptModuleForm) => {
      if (!promptModuleForm.dirty || promptModuleForm.disabled) {
        return;
      }

      const promptModuleNamespace = promptModuleForm.controls.namespace?.getRawValue();
      const isCapabilityManaged = capabilityForm.controls.managed.getRawValue();

      if (promptModuleNamespace?.globalNamespace || isCapabilityManaged) {
        // If a prompt module has been edited and it is either (1) global or (2) on a global capability:
        // Then we modify the prompt module to trigger creation of a copy in the local namespace.
        promptModuleForm.controls.namespace.setValue(localNamespace);
        promptModuleForm.controls.managed.setValue(false);
        promptModuleForm.controls.id.setValue(null);
        // Mark the capability dirty to trigger a copy of it in the local namespace with a reference to the new prompt module we'll be creating.
        capabilityForm.markAsDirty();
      }

      const promptModuleId = promptModuleForm.controls.id.getRawValue() ?? undefined;
      const content = promptModuleForm.getContent();

      if (promptModuleId || content) {
        // If prompt module doesn't exist yet, and there is no content, we don't need to create a prompt module
        promptModuleUpdates.push(
          this.aiAssistantService
            .upsertPromptModule(
              promptModuleForm.toPromptModule(localNamespace, capabilityForm.controls.name.getRawValue()),
              content ?? '',
            )
            .then((newPromptModuleId) => {
              // If the prompt module was created, set the ID on the form so it's available when associating the prompt module with the capability
              if (newPromptModuleId && !promptModuleForm.controls.id.getRawValue()) {
                promptModuleForm.controls.id.setValue(newPromptModuleId);
              }
              // Mark as pristine to avoid creating redundant versions of the prompt module on additional submits
              promptModuleForm.markAsPristine();
              return { promptModuleId: newPromptModuleId, success: true };
            })
            .catch(() => {
              return { promptModuleId: promptModuleId || 'unknown', success: false };
            }),
        );
      }
    });

    return Promise.all(promptModuleUpdates);
  }

  /**
   * Updates prompt modules from multiple capabilities (used in assistant form)
   */
  async updatePromptModulesFromCapabilities(
    capabilities: AiCapabilityForm[],
    options: FormSubmissionOptions,
  ): Promise<PromptModuleUpdateResult[]> {
    const promptModuleResults: PromptModuleUpdateResult[] = [];

    for (const capability of capabilities) {
      const results = await this.updatePromptModulesFromCapability(capability, options);
      promptModuleResults.push(...results);
    }

    return promptModuleResults;
  }

  /**
   * Updates a single capability
   */
  async updateCapability(
    capabilityForm: AiCapabilityForm,
    options: FormSubmissionOptions,
  ): Promise<CapabilityUpdateResult> {
    const localNamespace = getNamespace(options.accountGroupId, options.partnerId);
    if (!localNamespace) {
      throw new Error('Unable to determine local namespace');
    }

    // Only update the capability if it's dirty and enabled
    if (capabilityForm.dirty && capabilityForm.enabled && !capabilityForm.hasOnlyConfigurationChanges()) {
      const goal = capabilityForm.toGoal(localNamespace);
      const capabilityId = await this.aiAssistantService.upsertCapability(goal);

      // If the goal was created, set the ID on the form
      if (capabilityId && !capabilityForm.controls.goalId.getRawValue()) {
        capabilityForm.controls.goalId.setValue(capabilityId);
      }

      return { capabilityId: capabilityId || 'unknown', success: true };
    }

    return { capabilityId: 'none', success: true };
  }

  /**
   * Updates multiple capabilities
   */
  async updateCapabilities(
    capabilities: AiCapabilityForm[],
    options: FormSubmissionOptions,
  ): Promise<CapabilityUpdateResult[]> {
    const localNamespace = getNamespace(options.accountGroupId, options.partnerId);
    if (!localNamespace) {
      throw new Error('Unable to determine local namespace');
    }

    const capabilityUpdates: Promise<CapabilityUpdateResult>[] = [];

    const capabilitiesToUpdate = capabilities.filter(
      (capability) => capability.dirty && capability.enabled && !capability.hasOnlyConfigurationChanges(),
    );

    capabilitiesToUpdate.forEach((capability) => {
      const goal = capability.toGoal(localNamespace);
      capabilityUpdates.push(
        this.aiAssistantService
          .upsertCapability(goal)
          .then((id) => {
            // If the goal was created, set the ID on the form so it's available when associating the goal with the assistant
            if (id && !capability.controls.goalId.getRawValue()) {
              capability.controls.goalId.setValue(id);
            }
            return { capabilityId: id || 'unknown', success: true };
          })
          .catch(() => {
            return { capabilityId: 'unknown', success: false };
          }),
      );
    });

    return Promise.all(capabilityUpdates);
  }

  /**
   * Complete submission flow for a single capability (tools -> prompt modules -> capability)
   */
  async submitCapability(
    capabilityForm: AiCapabilityForm,
    options: FormSubmissionOptions,
  ): Promise<{
    tools: ToolUpdateResult[];
    promptModules: PromptModuleUpdateResult[];
    capability: CapabilityUpdateResult;
  }> {
    // Update tools first
    const toolResults = await this.updateToolsFromCapability(capabilityForm, options);

    // Update prompt modules
    const promptModuleResults = await this.updatePromptModulesFromCapability(capabilityForm, options);

    // Update capability (depends on tools and prompt modules)
    const capabilityResult = await this.updateCapability(capabilityForm, options);

    return {
      tools: toolResults,
      promptModules: promptModuleResults,
      capability: capabilityResult,
    };
  }

  /**
   * Complete submission flow for multiple capabilities (tools -> prompt modules -> capabilities)
   */
  async submitCapabilities(
    capabilities: AiCapabilityForm[],
    options: FormSubmissionOptions,
  ): Promise<{
    tools: ToolUpdateResult[];
    promptModules: PromptModuleUpdateResult[];
    capabilities: CapabilityUpdateResult[];
  }> {
    // Update tools first
    const toolResults = await this.updateToolsFromCapabilities(capabilities, options);

    // Update prompt modules
    const promptModuleResults = await this.updatePromptModulesFromCapabilities(capabilities, options);

    // Update capabilities (depends on tools and prompt modules)
    const capabilityResults = await this.updateCapabilities(capabilities, options);

    return {
      tools: toolResults,
      promptModules: promptModuleResults,
      capabilities: capabilityResults,
    };
  }
}
